<template>
  <div id="typhoon-layer">
  </div>
</template>

<script>
import mapboxgl from "mapbox-gl";
import * as turf from "@turf/turf";
import moment from "moment-timezone";
import {createNamespacedHelpers} from "vuex";

// Store
const {
  mapActions: mapActionsOfLiveMap,
  mapMutations: mapMutationsOfLiveMap,
  mapState: mapStateOfLiveMap,
  mapGetters: mapGettersOfLiveMap
} = createNamespacedHelpers("liveMap");

// Mixins
import {TyphoonMixin} from "@/mixins";

export default {
  name: "TyphoonLayer",
  mixins: [TyphoonMixin],
  data() {
    return {
      typhoonLayerIdsList: [],
      typhoonSourceIdsList: [],
      layersMinZoom: 3,
      circleSteps: 128,
      circleUnits: 'kilometers'
    };
  },
  props: {
    map: Object,
    isReady: {type: Boolean, default: false},
    mapEvent: String
  },
  computed: {
    ...mapGettersOfLiveMap([]),
    ...mapStateOfLiveMap({
      typhoonInfoList: state => state.typhoonInfoList,
      typhoonLayerVisibility: state => state.typhoonLayerVisibility,
    }),
  },
  watch: {
    mapEvent() {
    },
    isReady() {
      if (this.isReady) {
        this.setTyphoonLayers(this.typhoonInfoList)
      }
    },
    typhoonInfoList(typhoonInfoList) {
      if (typhoonInfoList) {
        this.setTyphoonLayers(typhoonInfoList)
      }
    },
    typhoonLayerVisibility(isShow) {
      this.setTyphoonVisibility(isShow)
    }
  },
  methods: {
    ...mapActionsOfLiveMap([]),
    ...mapMutationsOfLiveMap(["SET_OFFING_WAVE_POINTS"]),

    setTyphoonVisibility(isShow) {
      this.typhoonLayerIdsList.forEach(typhoonLayerIds => {
        typhoonLayerIds.forEach(layerId => {
          if (this.map.getLayer(layerId)) {
            this.map.setLayoutProperty(
              layerId,
              'visibility',
              isShow ? "visible" : "none"
            );
          }
        })
      })
    },

    async removeTyphoonSourceLayer() {
      this.typhoonLayerIdsList.forEach(typhoonLayerIds => {
        typhoonLayerIds.forEach(layerId => {
          if (this.map.getLayer(layerId)) this.map.removeLayer(layerId)
        })
      })
      this.typhoonSourceIdsList.forEach(typhoonSourceIds => {
        typhoonSourceIds.forEach(sourceId => {
          if (this.map.getSource(sourceId)) this.map.removeSource(sourceId)
        })
      })
      this.typhoonLayerIdsList = [];
      this.typhoonSourceIdsList = [];
      return true
    },

    async setTyphoonLayers(typhoonInfoList) {
      if (typhoonInfoList === null) return
      await this.removeTyphoonSourceLayer();

      typhoonInfoList.forEach((typhoonInfo, index) => {
        let layerIds = []
        let sourceIds = []
        let nowcast = typhoonInfo.nowcastData[0]
        let typhoonName = this.getTyphoonName(typhoonInfo.number, nowcast.props.class)

        // nowcast 強風域
        if (nowcast.props.strongWindArea[0].radius !== null) {
          let strongWindAreaRadius = nowcast.props.strongWindArea[0].radius
          let strongWindAreaCircleTranslate = [0, 0]
          if (nowcast.props.strongWindArea.length >= 2) {
            strongWindAreaRadius = ((nowcast.props.strongWindArea[0].radius - nowcast.props.strongWindArea[1].radius) / 2) + nowcast.props.strongWindArea[1].radius
            strongWindAreaCircleTranslate = this.calcCircleTranslate(nowcast.props.strongWindArea[0], nowcast.props.strongWindArea[1])
          }

          let center = nowcast.coordinate;
          let options = {steps: this.circleSteps, units: this.circleUnits, properties: {}};
          let windAreaCircle = turf.circle(center, strongWindAreaRadius, options);
          let nowcastStrongWindAreaSourceId = `nowcastStrongWindAreaSource${index}`
          sourceIds.push(nowcastStrongWindAreaSourceId)

          this.map.addSource(nowcastStrongWindAreaSourceId, {
            type: "geojson",
            data: {
              type: "FeatureCollection",
              features: [windAreaCircle]
            }
          });

          let typhoonNowcastStrongWindAreaCircleLayerId = `typhoonNowcastStrongWindAreaCircleLayer${index}`
          layerIds.push(typhoonNowcastStrongWindAreaCircleLayerId)
          this.map.addLayer({
            id: typhoonNowcastStrongWindAreaCircleLayerId,
            type: "fill",
            source: nowcastStrongWindAreaSourceId,
            minzoom: this.layersMinZoom,
            paint: {
              "fill-opacity": 0.35,
              "fill-color": "#D03EDD",
              "fill-outline-color": "#D03EDD",
              "fill-translate": strongWindAreaCircleTranslate
            }
          });
        }

        // nowcast 暴風警戒域
        if (nowcast.props.stormArea[0].radius !== null) {
          let stormAreaRadius = nowcast.props.stormArea[0].radius
          let stormAreaCircleTranslate = [0, 0]
          if (nowcast.props.stormArea.length >= 2) {
            stormAreaRadius = ((nowcast.props.stormArea[0].radius - nowcast.props.stormArea[1].radius) / 2) + nowcast.props.stormArea[1].radius
            stormAreaCircleTranslate = this.calcCircleTranslate(nowcast.props.stormArea[0], nowcast.props.stormArea[1])
          }

          let center = nowcast.coordinate;
          let options = {steps: this.circleSteps, units: this.circleUnits, properties: {}};
          let stormAreaCircle = turf.circle(center, stormAreaRadius, options);
          let nowcastStormAreaAreaSourceId = `nowcastStormAreaAreaSource${index}`
          sourceIds.push(nowcastStormAreaAreaSourceId)

          this.map.addSource(nowcastStormAreaAreaSourceId, {
            type: "geojson",
            data: {
              type: "FeatureCollection",
              features: [stormAreaCircle]
            }
          });

          let typhoonNowcastStormAreaCircleLayerId = `typhoonNowcastStormAreaCircleLayer${index}`
          layerIds.push(typhoonNowcastStormAreaCircleLayerId)
          this.map.addLayer({
            id: typhoonNowcastStormAreaCircleLayerId,
            type: "fill",
            source: nowcastStormAreaAreaSourceId,
            minzoom: this.layersMinZoom,
            paint: {
              "fill-opacity": 0.4,
              "fill-color": "#ED177E",
              "fill-outline-color": "#ED177E",
              "fill-translate": stormAreaCircleTranslate
            }
          });
        }

        // Foreacast 通過位置
        let foreacastTracks = []
        typhoonInfo.foreacastData.forEach((forecast, i) => {
          if(!isNaN(forecast.coordinate[0]) && !isNaN(forecast.coordinate[1])) {

            // Foreacast 暴風警戒域
            let forecastStormAreaRadius = forecast.props.stormArea[0].radius
            let forecastStormAreaCircleTranslate = [0, 0]
            if (forecast.props.stormArea.length >= 2) {
              forecastStormAreaRadius = ((forecast.props.stormArea[0].radius - forecast.props.stormArea[1].radius) / 2) + forecast.props.stormArea[1].radius
              forecastStormAreaCircleTranslate = this.calcCircleTranslate(forecast.props.stormArea[0], forecast.props.stormArea[1])
            }

            let forecastCenter = forecast.coordinate;
            let forecastOptions = {steps: this.circleSteps, units: this.circleUnits, properties: {}};
            let forecastStormAreaCircle = turf.circle(forecastCenter, forecastStormAreaRadius, forecastOptions);
            let forecastStormAreaAreaSourceId = `forecastStormAreaAreaSource${index}-${i}`
            sourceIds.push(forecastStormAreaAreaSourceId)

            this.map.addSource(forecastStormAreaAreaSourceId, {
              type: "geojson",
              data: {
                type: "FeatureCollection",
                features: [forecastStormAreaCircle]
              }
            });

            let typhoonForecastStormAreaCircleLayerId = `typhoonForecastStormAreaCircleLayer${index}-${i}`
            layerIds.push(typhoonForecastStormAreaCircleLayerId)
            this.map.addLayer({
              id: typhoonForecastStormAreaCircleLayerId,
              type: "fill",
              source: forecastStormAreaAreaSourceId,
              minzoom: this.layersMinZoom,
              paint: {
                "fill-opacity": 0.0,
                "fill-color": "#ED177E",
                "fill-outline-color": "#ED177E",
                "fill-translate": forecastStormAreaCircleTranslate
              }
            });

            let typhoonForecastStormAreaOutlineCircleLayerId = `typhoonForecastStormAreaOutlineCircleLayer${index}-${i}`
            layerIds.push(typhoonForecastStormAreaOutlineCircleLayerId)
            this.map.addLayer({
              id: typhoonForecastStormAreaOutlineCircleLayerId,
              type: "line",
              source: forecastStormAreaAreaSourceId,
              minzoom: this.layersMinZoom,
              layout: {},
              paint: {
                "line-color": "#ED177E",
                "line-width": 2.0
              }
            })

            foreacastTracks.push(forecast.coordinate)
            let foreacastPointSourceId = `foreacastPointSource${index}-${i}`
            let forecastDataProps = forecast.props
            forecastDataProps["typhoonName"] = typhoonName
            sourceIds.push(foreacastPointSourceId)
            this.map.addSource(foreacastPointSourceId, {
              type: "geojson",
              data: {
                type: "FeatureCollection",
                features: [turf.point(forecast.coordinate, forecastDataProps)]
              }
            });

            let forecastCircleRadius = forecast.props.centerPart.circle[0].radius
            let center = forecast.coordinate;
            let options = {steps: this.circleSteps, units: this.circleUnits, properties: {}};
            let forecastCircle = turf.circle(center, forecastCircleRadius, options);
            let nowcastStormAreaAreaSourceId = `nowcastStormAreaAreaSource${index}-${i}`
            sourceIds.push(nowcastStormAreaAreaSourceId)
            this.map.addSource(nowcastStormAreaAreaSourceId, {
              type: "geojson",
              data: {
                type: "FeatureCollection",
                features: [forecastCircle]
              }
            });

            let typhoonForecastCircleLayerId = `typhoonForecastCircleLayer${index}-${i}`
            layerIds.push(typhoonForecastCircleLayerId)
            this.map.addLayer({
              id: typhoonForecastCircleLayerId,
              type: "fill",
              source: nowcastStormAreaAreaSourceId,
              minzoom: this.layersMinZoom,
              paint: {
                "fill-opacity": 0.4,
                "fill-color": "#828282",
                "fill-outline-color": "#F2F2F2",
              }
            });

            let typhoonForecastCircleOutlineLayerId = `typhoonForecastCircleOutlineLayer${index}-${i}`
            layerIds.push(typhoonForecastCircleOutlineLayerId)
            this.map.addLayer({
              id: typhoonForecastCircleOutlineLayerId,
              type: 'line',
              source: nowcastStormAreaAreaSourceId,
              minzoom: this.layersMinZoom,
              layout: {},
              paint: {
                'line-color': '#F2F2F2',
                'line-width': 2.0,
              }
            })

            let typhoonForecastCenterCircleLayerId = `typhoonForecastCenterCircleLayer${index}-${i}`
            layerIds.push(typhoonForecastCenterCircleLayerId)
            this.map.addLayer({
              id: typhoonForecastCenterCircleLayerId,
              type: "circle",
              source: foreacastPointSourceId,
              minzoom: this.layersMinZoom,
              paint: {
                "circle-radius": 3,
                'circle-stroke-width': 1,
                "circle-stroke-color": "#E9ED17",
                "circle-color": "#E9ED17",
              }
            });

            this.cratePopup(typhoonForecastCenterCircleLayerId, true)

            // foreacast テキスト
            let timeText = moment.utc(forecast.props.time).local().format("MM/DD-HH:mm").split("-")
            let polarisTyphoonForecastSymbolTextLayerId = `polarisTyphoonForecastSymbolTextLayer${index}-${i}`
            layerIds.push(polarisTyphoonForecastSymbolTextLayerId)
            this.map.addLayer({
              id: polarisTyphoonForecastSymbolTextLayerId,
              type: "symbol",
              source: foreacastPointSourceId,
              minzoom: this.layersMinZoom,
              layout: {
                "text-field": `${timeText[0]}\n${timeText[1]}`,
                "text-anchor": "bottom",
                "text-size": 14,
                "text-offset": [2, 2],
              },
              paint: {
                "text-color": "#FFFFFF",
                "text-halo-color": "#000000",
                "text-halo-width": 1
              }
            });
          }

        })

        foreacastTracks.unshift(typhoonInfo.nowcastData[0].coordinate)
        let foreacastTrackSourceId = `foreacastTrackSource${index}`
        sourceIds.push(foreacastTrackSourceId)
        this.map.addSource(foreacastTrackSourceId, {
          type: "geojson",
          data: {
            type: "FeatureCollection",
            features: [turf.lineString(foreacastTracks)]
          }
        });

        let nowcastPointSourceSourceId = `nowcastPointSource${index}`
        sourceIds.push(nowcastPointSourceSourceId)
        let nowcastDataProps = typhoonInfo.nowcastData[0].props
        nowcastDataProps["typhoonName"] = typhoonName
        this.map.addSource(nowcastPointSourceSourceId, {
          type: "geojson",
          data: {
            type: "FeatureCollection",
            features: [turf.point(typhoonInfo.nowcastData[0].coordinate, nowcastDataProps)]
          }
        });

        // nowcast テキスト
        let timeText = moment.utc(nowcast.props.time).local().format("MM/DD-HH:mm").split("-")
        let polarisTyphoonNowcastsSymbolTextLayerId = `polarisTyphoonNowcastsSymbolTextLayer${index}`
        layerIds.push(polarisTyphoonNowcastsSymbolTextLayerId)
        this.map.addLayer({
          id: polarisTyphoonNowcastsSymbolTextLayerId,
          type: "symbol",
          source: nowcastPointSourceSourceId,
          minzoom: this.layersMinZoom,
          layout: {
            "text-field": `${typhoonName}\n${timeText[0]}\n${timeText[1]}`,
            "text-anchor": "bottom",
            "text-size": 14,
            "text-offset": [2, 5],
          },
          paint: {
            "text-color": "#FFFFFF",
            "text-halo-color": "#000000",
            "text-halo-width": 1
          }
        });

        // nowcast 中心点
        let typhoonNowcastCenterPointCircleLayerId = `typhoonNowcastCenterPointCircleLayer${index}`
        layerIds.push(typhoonNowcastCenterPointCircleLayerId)
        this.map.addLayer({
          id: typhoonNowcastCenterPointCircleLayerId,
          type: "circle",
          source: nowcastPointSourceSourceId,
          minzoom: this.layersMinZoom,
          paint: {
            "circle-radius": 5,
            'circle-stroke-width': 1,
            "circle-stroke-color": "#F2F2F2",
            "circle-color": "#F2F2F2",
          }
        });

        this.cratePopup(typhoonNowcastCenterPointCircleLayerId, false)

        // 通過位置
        if(typhoonInfo.historyTracks.length > 2) {
          let historyTrackSourceId = `historyTrackSource${index}`
          sourceIds.push(historyTrackSourceId)
          this.map.addSource(historyTrackSourceId, {
            type: "geojson",
            data: {
              type: "FeatureCollection",
              features: [turf.lineString(typhoonInfo.historyTracks)]
            }
          });

          let historyTyphoonTrackLayerId = `historyTyphoonTrackLayer${index}`
          layerIds.push(historyTyphoonTrackLayerId)
          this.map.addLayer({
            id: historyTyphoonTrackLayerId,
            type: "line",
            source: historyTrackSourceId,
            minzoom: this.layersMinZoom,
            paint: {
              "line-color": "#E0E0E0",
              "line-width": 2.0,
            }
          });
        }

        let foreacastTyphoonTrackLayerId = `foreacastTyphoonTrackLayer${index}`
        layerIds.push(foreacastTyphoonTrackLayerId)
        this.map.addLayer({
          id: foreacastTyphoonTrackLayerId,
          type: "line",
          source: foreacastTrackSourceId,
          minzoom: this.layersMinZoom,
          paint: {
            "line-color": "#E9ED17",
            "line-width": 2.0,
          }
        });

        this.typhoonLayerIdsList.push(layerIds)
        this.typhoonSourceIdsList.push(sourceIds)
      })
    },

    cratePopup(layerId, isForeacast) {
      let popup = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false
      });
      let self = this
      this.map.on('mouseenter', layerId, function (e) {
        self.map.getCanvas().style.cursor = 'pointer';
        let coordinates = e.features[0].geometry.coordinates.slice();
        let html = self.crateHtml(e.features[0].properties, isForeacast);
        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
          coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
        }
        popup
          .setLngLat(coordinates)
          .setHTML(html)
          .setMaxWidth("300px")
          .addTo(self.map);
      });
      this.map.on('mouseleave', layerId, function () {
        self.map.getCanvas().style.cursor = '';
        popup.remove();
      });

    },

    crateHtml(props, isForeacast) {
      let isForeacastVal = isForeacast ? "(予報)" : "(実況)"
      let centerPart = JSON.parse(props.centerPart)
      let wind = JSON.parse(props.wind)
      let areaClass = JSON.parse(props.areaClass)
      let intensityClass = JSON.parse(props.intensityClass)
      let stormArea
      let strongWindArea
      if (props.stormArea) {
        stormArea = JSON.parse(props.stormArea)
      }
      if (props.strongWindArea) {
        strongWindArea = JSON.parse(props.strongWindArea)
      }

      moment.utc(props.time).local().format("YYYY/MM/DD (dd) HH:mm")
      let time = moment.utc(props.time).local().format("YYYY/MM/DD (dd) HH:mm")
      return `<div id="typhoon-popup-container">
    <div id="item-time"><span class="aisea-text-14-700-black">${time}${isForeacastVal}</span></div>
      <div id="item-typhoon-name-val"><span class="aisea-text-14-700-black">${props.typhoonName}</span></div>
      <div id="item-coordinate-val"><span class="aisea-text-14-400-black">${this.getCoordinateVal(centerPart.centerCoordinate)}</span></div>
      <div id="item-location-label"><span class="aisea-text-14-700-black">位置</span></div>
      <div id="item-location-val"><span class="aisea-text-14-400-black">${this.getLocationVal(centerPart.centerLocation)}</span></div>
      <div id="item-pressure-label"><span class="aisea-text-14-700-black">中心気圧</span></div>
      <div id="item-pressure-val"><span class="aisea-text-14-400-black">${this.getPressureVal(centerPart.pressure)}</span></div>
      <div id="item-class-label"><span class="aisea-text-14-700-black">階級</span></div>
      <div id="item-class-val"><span class="aisea-text-14-400-black">${this.getTyphoonClassVal(props.class)}</span></div>
      <div id="item-area-class-label"><span class="aisea-text-14-700-black">大きさ</span></div>
      <div id="item-area-class-val"><span class="aisea-text-14-400-black">${this.getAreaClassVal(areaClass)}</span></div>
      <div id="item-intensity-class-label"><span class="aisea-text-14-700-black">強さ</span></div>
      <div id="item-intensity-class-val"><span class="aisea-text-14-400-black">${this.getIntensityClassVal(intensityClass)}</span></div>
      <div id="item-direction-label"><span class="aisea-text-14-700-black">進行方向</span></div>
      <div id="item-direction-val"><span class="aisea-text-14-400-black">${this.getCenterPartDirectionVal(centerPart.direction)}</span></div>
      <div id="item-speed-label"><span class="aisea-text-14-700-black">速さ</span></div>
      <div id="item-speed-val"><span class="aisea-text-14-400-black">${this.getSpeedVal(centerPart.speed)}</span></div>
      <div id="item-wind-speed-label"><span class="aisea-text-14-700-black">最大風速</span></div>
      <div id="item-wind-speed-val"><span class="aisea-text-14-400-black">${this.getWindSpeedVal(wind.windSpeed)}</span></div>
      <div id="item-max-wind-speed-label"><span class="aisea-text-14-700-black">最大瞬間風速</span></div>
      <div id="item-max-wind-speed-val"><span class="aisea-text-14-400-black">${this.getMaxWindSpeedVal(wind.maximumWindSpeed)}</span></div>
      <div id="item-storm-area-label"><span class="aisea-text-14-700-black">暴風域</span></div>
      <div id="item-storm-area-val "><span class="aisea-text-14-400-black">${this.getStormAreaVal(stormArea)}</span></div>
      <div id="item-strong-wind-area-label"><span class="aisea-text-14-700-black">強風域</span></div>
      <div id="item-strong-wind-area-val"><span class="aisea-text-14-400-black">${this.getStrongWindAreaVal(strongWindArea)}</span></div>
      </div>`
    },

    metersToPixelsAtMaxZoom(meters, latitude) {
      return meters / 0.060 / Math.cos(latitude * Math.PI / 180)
    },
  }
};
</script>

<style lang="scss">
#typhoon-popup-container {
  display: grid;
  margin: 2px;
  grid-template-rows: 20px 20px;
  grid-template-columns: 100px 300px;
  grid-template-areas:
        "time time"
        "typhoon-name-val typhoon-name-val"
        "coordinate-val coordinate-val"
        "location-label location-val"
        "pressure-label pressure-val "
        "class-label class-val"
        "area-class-label area-class-val"
        "intensity-class-label intensity-class-val"
        "direction-label direction-val"
        "speed-label speed-val"
        "wind-speed-label wind-speed-val"
        "max-wind-speed-label max-wind-speed-val"
        "storm-area-label storm-area-val"
        "strong-wind-area-label strong-wind-area-val";
}

/* アイテム */
#item-time {
  grid-area: time;
}

#item-typhoon-name-val {
  grid-area: typhoon-name-val;
}

#item-coordinate-val {
  grid-area: coordinate-val;
  margin-bottom: 8px;
}

#item-location-label {
  grid-area: location-label;
}

#item-location-val {
  grid-area: location-val;
}

#item-pressure-label {
  grid-area: pressure-label;
}

#item-pressure-val {
  grid-area: pressure-val;
}

#item-class-label {
  grid-area: class-label;
}

#item-class-val {
  grid-area: class-val;
}

#item-area-class-label {
  grid-area: area-class-label;
}

#item-area-class-val {
  grid-area: area-class-val;
}

#item-intensity-class-label {
  grid-area: intensity-class-label;
}

#item-intensity-area-class-val {
  grid-area: intensity-class-val;
}


#item-direction-label {
  grid-area: direction-label;
}

#item-direction-val {
  grid-area: direction-val;
}

#item-speed-label {
  grid-area: speed-label;
}

#item-speed-val {
  grid-area: speed-val;
}

#item-wind-speed-label {
  grid-area: wind-speed-label;
}

#item-wind-speed-val {
  grid-area: wind-speed-val;
}

#item-max-wind-speed-label {
  grid-area: max-wind-speed-label;
}

#item-max-wind-speed-val {
  grid-area: max-wind-speed-val;
}

#item-strong-wind-area-label {
  grid-area: strong-wind-area-label;
}

#item-strong-wind-area-val {
  grid-area: strong-wind-area-val;
}

#item-storm-area-label {
  grid-area: storm-area-label;
}

#item-storm-area-val {
  grid-area: storm-area-val;
}


</style>
